import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { cn } from '../lib/utils';
import { useToast } from '../hooks/use-toast';
import { fetchFormations, submitFormationLead } from '../lib/api';
import { formations as mockFormations } from '../mock/mockData';
import { cleanFormationTitle } from '../lib/formationDisplay';
import { PROFESSION_LABELS } from '../lib/professionLabels';
import { CheckCircle2, Phone, ShieldCheck, Zap } from 'lucide-react';

const selectClassName = cn(
  'flex min-h-10 h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base shadow-sm ring-offset-background',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0',
  'disabled:cursor-not-allowed disabled:opacity-50',
);

const isDev = process.env.NODE_ENV === 'development';

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const [catalog, setCatalog] = useState(() => (isDev ? mockFormations : []));
  useEffect(() => {
    fetchFormations()
      .then((d) => {
        if (d?.length) setCatalog(d);
        else if (isDev) setCatalog(mockFormations);
      })
      .catch(() => {
        if (isDev) setCatalog(mockFormations);
      });
  }, []);

  const formationFromQuery = useMemo(() => {
    const fid = searchParams.get('formation');
    if (!fid) return null;
    const num = Number(fid);
    return catalog.find((f) => f.id === num) || null;
  }, [searchParams, catalog]);

  const [formationId, setFormationId] = useState(() =>
    formationFromQuery ? String(formationFromQuery.id) : '',
  );

  useEffect(() => {
    if (formationFromQuery) setFormationId(String(formationFromQuery.id));
  }, [formationFromQuery]);

  const selectedFormation = useMemo(() => {
    if (!formationId) return null;
    const id = Number(formationId);
    return catalog.find((f) => f.id === id) || null;
  }, [formationId, catalog]);

  const { toast } = useToast();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    profession: '',
    newsletter: true,
    cgu: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.cgu) {
      toast({ title: 'CGU requises', description: 'Veuillez accepter les conditions générales.', variant: 'destructive' });
      return;
    }
    if (!selectedFormation) {
      toast({ title: 'Formation requise', description: 'Choisissez une formation dans la liste.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { firstName, lastName, email, phone, profession, newsletter } = form;
      const res = await submitFormationLead({
        firstName,
        lastName,
        email,
        phone,
        profession,
        newsletter,
        formationId: selectedFormation.id,
        formationTitle: cleanFormationTitle(selectedFormation.title),
        formationCode: selectedFormation.code,
        formationType: selectedFormation.type,
        formationCategory: selectedFormation.category,
      });
      toast({
        title: 'Demande bien reçue',
        description: `Merci ${firstName}. Un conseiller Medelia vous rappelle très rapidement aux coordonnées indiquées.`,
      });
      if (res?.emailNote && process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.info('[Medelia] E-mail interne non envoyé :', res.emailNote);
      }
      setForm({ firstName: '', lastName: '', email: '', phone: '', profession: '', newsletter: true, cgu: false });
      setFormationId(formationFromQuery ? String(formationFromQuery.id) : '');
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || "Impossible d'envoyer votre demande. Réessayez.",
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const registerDescription = selectedFormation
    ? `Demande d’inscription à « ${cleanFormationTitle(selectedFormation.title)} » : laissez vos coordonnées, un conseiller Medelia vous rappelle pour finaliser votre dossier et le financement (France).`
    : 'Inscription à une formation Medelia : laissez vos coordonnées, un conseiller vous rappelle pour finaliser votre dossier et le financement (DPC, FIFPL, OPCO, CPF).';

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="S’inscrire à une formation — rappel conseiller"
        description={registerDescription}
        canonicalPath="/inscription"
      />
      <Navbar />
      <main className="safe-pt-nav">
        <section className="py-16 bg-gradient-to-br from-teal-50/60 via-white to-emerald-50/40">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="text-teal-600 text-sm font-bold uppercase tracking-widest">Inscription</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-3 tracking-tight leading-tight">
                S'inscrire à une formation
              </h1>
              <p className="text-slate-600 mt-4 mb-8">
                Renseignez vos coordonnées : un conseiller vous rappelle rapidement pour finaliser votre dossier (pas de mot de passe ni
                de compte à créer ici).
              </p>

              <div className="space-y-4">
                {[
                  { icon: Phone, title: 'Rappel rapide', desc: 'Un conseiller vous contacte en général sous 24h ouvrées.' },
                  { icon: ShieldCheck, title: 'Simple et sécurisé', desc: 'Vos informations servent uniquement à vous recontacter pour cette formation.' },
                  { icon: Zap, title: 'Financement', desc: 'Nous vous guidons sur DPC, FIFPL, OPCO ou CPF selon votre situation.' },
                ].map((it, i) => (
                  <div key={i} className="flex items-start gap-4 bg-white border border-slate-200 rounded-xl p-4">
                    <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                      <it.icon className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 text-sm">{it.title}</p>
                      <p className="text-xs text-slate-600 mt-0.5">{it.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-xl shadow-slate-900/5">
              <h2 className="text-2xl font-bold text-slate-900 mb-1">Vos informations</h2>
              <p className="text-sm text-slate-500 mb-6">Gratuit • Sans engagement</p>

              {selectedFormation && (
                <div className="mb-6 rounded-xl border border-teal-100 bg-teal-50/80 px-4 py-3 text-sm text-slate-700">
                  <p className="font-semibold text-teal-900">Formation concernée</p>
                  <p className="mt-1 leading-snug text-slate-800">{cleanFormationTitle(selectedFormation.title)}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="formation">Formation *</Label>
                  <select
                    id="formation"
                    value={formationId}
                    onChange={(e) => setFormationId(e.target.value)}
                    required
                    className={selectClassName}
                  >
                    <option value="" disabled>
                      Choisissez une formation
                    </option>
                    {catalog.map((f) => (
                      <option key={f.id} value={String(f.id)}>
                        {cleanFormationTitle(f.title)}
                      </option>
                    ))}
                  </select>
                  {!formationFromQuery && (
                    <p className="text-xs text-slate-500">
                      Parcourir le{' '}
                      <Link to="/formations" className="text-teal-700 font-medium underline-offset-2 hover:underline">
                        catalogue
                      </Link>
                      .
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fn">Prénom *</Label>
                    <Input id="fn" required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ln">Nom *</Label>
                    <Input id="ln" required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="em">Email professionnel *</Label>
                  <Input id="em" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ph">Téléphone *</Label>
                  <Input id="ph" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Pour vous rappeler" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pr">Profession *</Label>
                  <select
                    id="pr"
                    value={form.profession}
                    onChange={(e) => setForm({ ...form, profession: e.target.value })}
                    required
                    className={selectClassName}
                  >
                    <option value="" disabled>
                      Sélectionnez votre profession
                    </option>
                    {Object.entries(PROFESSION_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-start gap-2 pt-2">
                  <Checkbox id="nl" checked={form.newsletter} onCheckedChange={(c) => setForm({ ...form, newsletter: !!c })} />
                  <label htmlFor="nl" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                    Je souhaite recevoir la newsletter Medelia (nouvelles formations, actualités DPC).
                  </label>
                </div>
                <div className="flex items-start gap-2">
                  <Checkbox id="cgu" checked={form.cgu} onCheckedChange={(c) => setForm({ ...form, cgu: !!c })} />
                  <label htmlFor="cgu" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                    J'accepte les conditions générales d'utilisation et la politique de confidentialité. *
                  </label>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  size="lg"
                  className="mt-2 w-full min-h-11 touch-manipulation bg-teal-600 hover:bg-teal-700 text-white shadow-md shadow-teal-600/20"
                >
                  {loading ? 'Envoi en cours...' : "S'inscrire"}
                </Button>
                <div className="flex items-center justify-center gap-2 pt-2 text-xs text-slate-500">
                  <CheckCircle2 className="w-3.5 h-3.5 text-teal-600" /> Données traitées conformément au RGPD
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default RegisterPage;
